import { PulseIndicator } from "@/components/common/pulse-indicator";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import {
  useGetExecution,
  useGetFlowExecutionsAsInfinite,
} from "@/queries/flows/flows.api";
import { useGetApplicantsAsInfinite } from "@/queries/users/applicants.api";
import { filterTypesEnum, type FilterOption } from "@/types/common/filters";
import { formatDateTime } from "@/utils/dates";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { Button, Icon, SearchableSelect } from "@elaniintc/goes-design-system";
import { parseAsString, useQueryState } from "nuqs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

const defaultFilters: (val: {
  status?: string;
  updated_at?: string;
  applicant_id?: string;
  context?: string;
  search?: string;
}) => FilterOption[] = ({
  status = "active",
  updated_at = "",
  applicant_id = "",
  context = "",
  search = "",
}: {
  status?: string;
  updated_at?: string;
  applicant_id?: string;
  context?: string;
  search?: string;
}) => [
  {
    id: 0,
    field: "search",
    type: filterTypesEnum.basicsearch,
    label: "Búsqueda",
    isUpdated: search !== "",
    result: search,
  },
  {
    id: 1,
    field: "status",
    type: filterTypesEnum.status,
    label: "Estado",
    isUpdated: status !== "",
    result: status,
    status: [
      {
        field: "UNPROCESSED",
        label: "No procesados",
        value: status.includes("UNPROCESSED"),
      },
      {
        field: "RUNNING",
        label: "En proceso",
        value: status.includes("RUNNING"),
      },
      {
        field: "PENDING",
        label: "Pendientes",
        value: status.includes("PENDING"),
      },
      {
        field: "COMPLETED",
        label: "Completados",
        value: status.includes("COMPLETED"),
      },
      {
        field: "FAILED",
        label: "Fallidos",
        value: status.includes("FAILED"),
      },
      {
        field: "EXPIRED",
        label: "Expirados",
        value: status.includes("EXPIRED"),
      },
      {
        field: "CANCELED",
        label: "Cancelados",
        value: status.includes("CANCELED"),
      },
    ],
  },
  {
    id: 2,
    field: "created_at",
    type: filterTypesEnum.dates,
    label: "Fecha de creación",
    shouldDisabledFutureDates: true,
    isUpdated: updated_at !== "",
    result: updated_at,
  },
  {
    id: 3,
    label: "Tramitador",
    type: filterTypesEnum.textsearch,
    searchType: "user",
    field: "applicant_id",
    searchField: "search",
    result: applicant_id,
    isUpdated: applicant_id !== "",
    query: useGetApplicantsAsInfinite,
  },
  {
    id: 4,
    label: "Contexto",
    type: filterTypesEnum.status,
    field: "context",
    result: context,
    isUpdated: context !== "",
    status: [
      {
        field: "SANDBOX",
        label: "Sandbox",
        value: context.includes("SANDBOX"),
      },
      {
        field: "TESTING",
        label: "Testing",
        value: context.includes("TESTING"),
      },
      {
        field: "LIVE",
        label: "Live",
        value: context.includes("LIVE"),
      },
    ],
  },
];
// /applicants

const ExecutionSelection = () => {
  const params = useParams<{
    id: string;
    flowId: string;
  }>();
  const [executionId, setExecutionId] = useQueryState(
    "execution_id",
    parseAsString,
  );
  const [modalState, setModalState] = useState<"idle" | "filters">("idle");
  const [status, setStatus] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [user, setUser] = useState<string | null>(null);
  const [context, setContext] = useState<string | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [formatFilters, setFormatFilters] = useState<FilterOption[]>(
    defaultFilters({
      status: "",
      updated_at: "",
      applicant_id: "",
      search: "",
    }),
  );

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useGetFlowExecutionsAsInfinite({
      flow_id: params.flowId || "",
      applicant_id: user || "",
      status: status || "",
      created_at: updatedAt || "",
      context: context || "",
      search: search || "",
    });

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.field === "status") {
          setStatus(f.result);
        } else if (f.field === "updated_at") {
          setUpdatedAt(f.result);
        } else if (f.field === "applicant_id") {
          setUser(f.result);
        } else if (f.field === "context") {
          setContext(f.result);
        } else if (f.field === "search") {
          setSearch(f.result);
        }
      }
    });

    // Set filter to updated false if result is empty
    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters(
      defaultFilters({
        status: "",
        updated_at: "",
        applicant_id: "",
        context: "",
        search: "",
      }),
    );
    setStatus(null);
    setUpdatedAt(null);
    setUser(null);
    setContext(null);
    setSearch(null);
    setModalState("idle");
  };
  const hasFilters = formatFilters.some((filter) => filter.isUpdated);

  const {
    data: executionData,
    isRefetching,
    refetch: refetchExecution,
  } = useGetExecution(executionId || "", 10000);

  const { setExecutionData } = useWorkflowStore();

  useEffect(() => {
    if (executionData) {
      setExecutionData(executionData.data);
    }
  }, [executionData, setExecutionData]);

  const navigate = useNavigate();

  return (
    <div className="absolute w-[calc(100%-32px)] top-3 inset-x-4 mx-auto z-10 border border-neutral bg-neutral-object rounded-md p-3 flex justify-between items-center gap-4 h-[98px]">
      {executionData ? (
        <>
          {isFetching ? (
            <div>
              <Icon
                name="spinner-third"
                type="fas"
                className="text-on-disabled animate-spin"
              />
            </div>
          ) : (
            <div className="flex gap-1 items-center">
              <div>
                <p className="text-on-solid">
                  {executionData?.data?.formality_requests[0]?.code}
                </p>
                <p className="text-on-solid">
                  Estado: {executionData.data.status}
                </p>
                <p className="text-on-solid">
                  Creado el {formatDateTime(executionData.data.created_at)}
                </p>
              </div>
              {isRefetching && (
                <div className="text-on-solid">
                  <Icon
                    name="spinner-third"
                    type="fas"
                    className="text-on-disabled animate-spin"
                  />
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div />
      )}
      <div className="flex gap-2 items-center">
        <Button
          variant="quiet"
          size="icon"
          aria-label="refresh"
          onClick={() => {
            refetchExecution();
          }}
        >
          <Icon type="far" name="arrows-rotate" className="text-current" />
        </Button>
        <Button
          variant="quiet"
          onClick={() => {
            const flowId = params.flowId;
            if (flowId) {
              navigate(`/procesos/${params.id}/flujo/${flowId}`);
            }
          }}
        >
          Ir al flujo
          <Icon type="far" name="arrow-up-right" />
        </Button>
        <div className="w-[300px]">
          <SearchableSelect
            placeholder="Selecciona una ejecución"
            isLoading={isFetching}
            disabled={isFetching}
            hasMore={hasNextPage || false}
            loadMore={() => {
              if (hasNextPage) {
                fetchNextPage();
              }
            }}
            onSearch={(val) => {
              setSearch(val);
            }}
            value={executionId || ""}
            onChange={(val) => {
              const execution = data?.pages
                .flatMap((page) => page.data)
                .find((execution) => execution.id === val);
              if (execution) {
                setExecutionId(execution.id);
              }
            }}
            label=""
            srOnlyLabel
            name="execution-select"
            options={
              data?.pages.flatMap((page) => page.data).length === 0
                ? []
                : data?.pages
                    .flatMap((page) => page.data)
                    .map((option) => ({
                      label: option.formality_requests[0]?.code || "",
                      value: option.id,
                      description: option.status,
                    })) || []
            }
          />
        </div>
        {/* <Select
          onValueChange={(val) => {
            const execution = data?.data.find(
              (execution) => execution.id === val,
            );
            if (execution) {
              setExecutionId(execution.id);
            }
          }}
        >
          <SelectTrigger
            className="w-[350px]"
            isLoading={isFetching}
            disabled={isFetching}
            value={executionId || ""}
          >
            {!executionId ? (
              <p className="text-size-body text-on-surface-variant">
                Selecciona una ejecución
              </p>
            ) : (
              executionData?.data?.formality_requests[0]?.code || ""
            )}
          </SelectTrigger>
          <SelectContent className="max-h-[340px] max-w-[var(--radix-select-trigger-width)] overflow-y-auto border border-neutral py-2">
            {data?.data.length === 0 && (
              <p className="text-center text-on-disabled">
                {hasFilters
                  ? "No hay ejecuciones con los filtros aplicados"
                  : "No hay ejecuciones creadas"}
              </p>
            )}
            {(data?.data || []).map((option) => (
              <SelectItem
                key={option.id}
                value={option.id}
                textClassName="block"
                className="[&>*]:block"
              >
                <div className="w-full flex items-center justify-between gap-2">
                  <span className="text-body font-semibold">
                    {option.formality_requests[0]?.code}
                  </span>
                  <span className="text-body-quiet">{option.status}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select> */}
        <Button
          data-testid="filter-button"
          variant="quiet"
          type="button"
          onClick={() => {
            setModalState("filters");
          }}
          className="relative h-full"
        >
          <span className="hidden md:inline-block">Filtros</span>
          <Icon type="far" name="filter" className="text-current" />
          <PulseIndicator showIndicator={hasFilters} />
        </Button>
        {modalState === "filters" && (
          <FiltersModal
            isOpen={modalState === "filters"}
            onToggle={onCloseFilters}
            hasCurrentFilters={hasFilters}
            items={formatFilters}
            onApply={(newFilters) => {
              const filters = onApplyFilters(newFilters);
              setFormatFilters(filters);
              setModalState("idle");
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ExecutionSelection;
